/**
 * JavaScript base64 / base64url encoder and decoder
 */

// Base64 and Base64url dictionaries
const b64c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'; // base64 dictionary
const b64u = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'; // base64url dictionary
const b64pad = '='; // padding character

/**
 * Internal helper to encode data to base64 using a specified dictionary.
 * @param data - The UTF-8 encoded string data to encode.
 * @param len - The length of the data string.
 * @param b64x - The base64 dictionary to use (b64c for standard, b64u for url-safe).
 * @returns The base64 encoded string.
 */
function base64_encode_data(data: string, len: number, b64x: string): string {
  let dst = '';
  let i: number;

  // Process data in chunks of 3 bytes (4 base64 characters)
  for (i = 0; i <= len - 3; i += 3) {
    const byte1 = data.charCodeAt(i);
    const byte2 = data.charCodeAt(i + 1);
    const byte3 = data.charCodeAt(i + 2);

    // Bitwise operations for base64 encoding are standard and correct here
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(byte1 >>> 2);
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(((byte1 & 3) << 4) | (byte2 >>> 4));
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(((byte2 & 15) << 2) | (byte3 >>> 6));
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(byte3 & 63);
  }

  // Handle remaining bytes (1 or 2)
  if (len % 3 === 2) {
    const byte1 = data.charCodeAt(i);
    const byte2 = data.charCodeAt(i + 1);

    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(byte1 >>> 2);
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(((byte1 & 3) << 4) | (byte2 >>> 4));
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(((byte2 & 15) << 2));
    dst += b64pad;
  } else if (len % 3 === 1) {
    const byte1 = data.charCodeAt(i);

    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(byte1 >>> 2);
    // eslint-disable-next-line no-bitwise
    dst += b64x.charAt(((byte1 & 3) << 4));
    dst += b64pad;
    dst += b64pad;
  }

  return dst;
}

/**
 * Encode a JavaScript string to standard base64.
 * The input string is first converted from JavaScript UCS-2 to UTF-8.
 * @param str - The string to encode.
 * @returns The base64 encoded string.
 * @deprecated Uses legacy `unescape` and `encodeURIComponent`. Consider using `btoa` and `TextEncoder` for modern environments.
 */
export function base64_encode(str: string): string {
  // Convert UCS-2/UTF-16 string to UTF-8 bytes represented as a string
  // Note: `unescape` and `encodeURIComponent` are legacy. Modern equivalent:
  // const utf8Bytes = new TextEncoder().encode(str);
  // But `base64_encode_data` expects a string of bytes, so adaptation is needed.
  const utf8str: string = unescape(encodeURIComponent(str));
  return base64_encode_data(utf8str, utf8str.length, b64c);
}

/**
 * Encode a JavaScript string to base64url (URL-safe base64).
 * The input string is first converted from JavaScript UCS-2 to UTF-8.
 * @param str - The string to encode.
 * @returns The base64url encoded string (without padding by default in many contexts, but this includes padding).
 * @deprecated Uses legacy `unescape` and `encodeURIComponent`. Consider modern alternatives.
 */
export function base64url_encode(str: string): string {
  const utf8str: string = unescape(encodeURIComponent(str));
  return base64_encode_data(utf8str, utf8str.length, b64u);
}

/**
 * Internal helper to translate a base64 character to its integer index (0-63).
 * Handles both standard (+, /) and URL-safe (-, _) characters.
 * @param c - The base64 character.
 * @returns The integer index (0-63) or -1 if not found.
 */
function base64_charIndex(c: string): number {
  if (c === '+') return 62;
  if (c === '/') return 63;
  // Uses b64u.indexOf which covers [A-Za-z0-9-_] (returns 62 for '-' and 63 for '_')
  return b64u.indexOf(c);
}

/**
 * Decode a base64 or base64url string to a JavaScript string.
 * Input is assumed to be a base64/base64url encoded UTF-8 string.
 * The returned result is a JavaScript (UCS-2/UTF-16) string.
 * @param data - The base64 or base64url encoded string to decode.
 * @returns The decoded JavaScript string.
 * @throws {Error} If the input contains invalid base64 characters.
 * @deprecated Uses legacy `escape` and `decodeURIComponent`. Consider using `atob` and `TextDecoder` for modern environments.
 */
export function base64_decode(date: string): string {
  let dst = '';
  let i: number;

  // Remove whitespace (common in base64 strings)
  const data = date.replace(/\s/g, '');

  // Check if the string is valid base64 length (after removing padding)
  const len = data.length;
  if (len % 4 !== 0) {
    throw new Error('Invalid base64 string length');
  }

  for (i = 0; i < len; i += 4) {
    const a = base64_charIndex(data.charAt(i));
    const b = base64_charIndex(data.charAt(i + 1));

    // Check for invalid characters
    if (a === -1 || b === -1) {
      throw new Error(`Invalid base64 character at position ${i} or ${i + 1}`);
    }

    // First byte
    // eslint-disable-next-line no-bitwise
    dst += String.fromCharCode((a << 2) | (b >>> 4));

    if (i + 2 < len) {
      const c = base64_charIndex(data.charAt(i + 2));
      if (c === -1) {
        throw new Error(`Invalid base64 character at position ${i + 2}`);
      }
      // Second byte (if not padding)
      if (data.charAt(i + 2) !== b64pad) {
        // eslint-disable-next-line no-bitwise
        dst += String.fromCharCode(((b << 4) & 0xF0) | ((c >>> 2) & 0x0F));
      }

      if (i + 3 < len) {
        const d = base64_charIndex(data.charAt(i + 3));
        if (d === -1) {
          throw new Error(`Invalid base64 character at position ${i + 3}`);
        }
        // Third byte (if not padding)
        if (data.charAt(i + 3) !== b64pad) {
          // eslint-disable-next-line no-bitwise
          dst += String.fromCharCode(((c << 6) & 0xC0) | d);
        }
      }
    }
  }

  // Convert the UTF-8 bytes (represented as a string) back to UCS-2/UTF-16
  // Note: `escape` and `decodeURIComponent` are legacy.
  // Modern equivalent: const decodedString = new TextDecoder().decode(utf8Bytes);
  return decodeURIComponent(escape(dst));
}

/**
 * Check whether a specified base64 string contains base64url specific characters.
 * @param base64 - The base64 string to check.
 * @returns `true` if the string contains '-' or '_', indicating it might be base64url encoded; `false` otherwise.
 */
export function base64url_sniff(base64: string): boolean {
  return base64.includes('-') || base64.includes('_');
}

// --- Optional: Modern Alternatives (For Reference) ---

/**
 * Modern base64url encode using TextEncoder and btoa (Browser) / Buffer (Node.js)
 * This version does not add padding, which is typical for base64url.
 */
/*
export function modern_base64url_encode(str: string): string {
    // Convert string to UTF-8 bytes
    const encoder = new TextEncoder()
    const utf8Bytes = encoder.encode(str)
    // Convert bytes to base64 string
    const base64Str = btoa(String.fromCharCode(...utf8Bytes)) // Browser
    // For Node.js: const base64Str = Buffer.from(utf8Bytes).toString('base64')
    // Replace '+' -> '-', '/' -> '_', and remove padding '='
    return base64Str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
*/

/**
 * Modern base64url decode using atob (Browser) / Buffer (Node.js) and TextDecoder
 */
/*
export function modern_base64url_decode(base64url: string): string {
    // Add padding if necessary
    base64url = base64url.replace(/-/g, '+').replace(/_/g, '/')
    while (base64url.length % 4) base64url += '='
    // Decode base64 to bytes
    const binaryStr = atob(base64url) // Browser
    // For Node.js: const binaryStr = Buffer.from(base64url, 'base64').toString('binary')
    const bytes = new Uint8Array(binaryStr.length)
    for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i)
    }
    // Convert UTF-8 bytes to string
    const decoder = new TextDecoder()
    return decoder.decode(bytes)
}
*/
